import {
  AfterViewInit, ChangeDetectionStrategy,
  Component,
  EventEmitter, Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation, inject,
} from '@angular/core';
import { FileType } from '../file.type';
import { MIME_TYPES } from '../mime-types.const';
import { FileService, ToastService } from 'taxtank-core';
import { AbstractControlComponent } from '../../abstract-control/abstract-control.component';
import { Observable } from 'rxjs';
import { FileUpload } from 'primeng/fileupload';
import { FILE_TYPE_LABELS } from '@shared/component/form-controls/file/file-type-labels';

/**
 * Abstract wrapper over PrimeNG FileUpload with validation, Angular forms integration, and multiple upload modes support.
 * https://primeng.org/fileupload
 */

@Component({
  selector: 'app-abstract-file-uploader-control',
  templateUrl: './abstract-file-uploader-control.component.html',
  styleUrl: './abstract-file-uploader-control.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AbstractFileUploaderControlComponent<Model> extends AbstractControlComponent<Model> implements OnInit, AfterViewInit {
  @ViewChild(FileUpload, { static: false }) pFileUpload: FileUpload;

  /**
   * Custom text listing types
   */
  acceptLabel?: string;

  /**
   * Flag enable/disable multiple files upload
   */
  multiple = false;

  /**
   * List of file types accepted to upload
   */
  @Input() fileTypes: FileType[] = ['image', 'document', 'csv', 'excel'];

  /**
   * Maximum file size in megabytes
   */
  @Input() maxFileSizeMb = 25;

  /**
   * Prime file uploader UI mode
   */
  @Input() mode: 'button' | 'dragdrop' | 'icon' = 'button';

  /**
   * Visual size of the dragdrop mode
   */
  @Input() size: 'default' | 'small' | 'big' = 'default';

  @Input() caption = 'link';
  @Input() subLabel = '';
  @Output() changed: EventEmitter<void> = new EventEmitter<void>();
  @Output() added: EventEmitter<File[]> = new EventEmitter<File[]>();
  @Input() chooseIcon = 'icon-upload-cloud';

  /**
   * Maximum number of files accepted at once, null means no limit
   */
  @Input() maxFilesCount: number = null;

  /**
   * Mime types accepted by file input
   */
  accept: string;

  /**
   * Maximum file size in bytes
   */
  maxFileSize: number;

  isDragging: boolean;

  fileService = inject(FileService);
  toastService = inject(ToastService);

  /**
   * set prime file uploader validators
   */
  ngOnInit(): void {
    this.accept = this.fileTypes.map((fileType: FileType) => MIME_TYPES[fileType]).join();
    this.acceptLabel = this.fileTypes.map((fileType: FileType) => FILE_TYPE_LABELS[fileType]).join(', ');
    this.maxFileSize = this.maxFileSizeMb * 1000000;
  }

  /**
   * Overwrite prime file uploader methods
   */
  ngAfterViewInit(): void {
    this.pFileUpload.upload = this.upload.bind(this);
    this.pFileUpload.validate = this.validate.bind(this);
  }

  /**
   * Custom file validation method for prime file uploader
   */
  protected validate(file: File): boolean {
    this.pFileUpload.msgs = [];

    // validate file type
    if (this.accept && !this.accept.includes(file.type)) {
      const text = `${file.name}: Invalid file type`;
      this.pFileUpload.msgs.push({
        severity: 'error',
        text
      });

      // basic mode has no place for error messages, so we show toasts
      if (this.mode === 'button') {
        this.toastService.error(text);
      }

      return false;
    }

    // validate file size
    if (this.maxFileSize && file.size > this.maxFileSize) {
      const text = `${file.name}: Invalid file size`;
      this.pFileUpload.msgs.push({
        severity: 'error',
        text
      });

      // basic mode has no place for error messages, so we show toasts
      if (this.mode === 'button') {
        this.toastService.error(text);
      }

      return false;
    }

    return true;
  }

  protected abstract uploadFn(): Observable<Model>;
  /**
   * Custom file upload method for prime file uploader
   */
  protected upload(): void {
    this.uploadFn().subscribe({
      next: (files: Model) => {
        this.updateValue(files);
        this.onChange(this.value);
        this.pFileUpload.clear();
        this.changeDetectorRef.markForCheck();
        this.changed.emit();
      },
      error: (error: any) => this.fileService.handleError(error)
    });
  }

  /**
   * Updates the internal form control value with the upload result.
   * The default flow passes uploaded files, a subclass with its own `upload()` passes whatever it produced
   */
  protected abstract updateValue(uploaded: Model): void;

  protected abstract get isEmpty(): boolean;

  onAdd(): void {
    const files: File[] = [...(this.pFileUpload?.files ?? [])];

    this.pFileUpload.files = [];
    this.pFileUpload.clearInputElement();

    if (files.length) {
      this.added.emit(files);
    }
  }

  clear(): void {
    this.value = null;
    this.onChange(null);
    this.pFileUpload.clear();
  }

  ondragenter(e): void {
    this.isDragging = true;
    e.stopPropagation();
    e.preventDefault();
  }

  ondragleave(e): void {
    this.isDragging = false;
    e.stopPropagation();
    e.preventDefault();
  }

  ondrop(e): void {
    this.isDragging = false;
    this.pFileUpload.onDrop(e);
  }
}

